/**
 * Word-level marks inside one cue's text. PRESENTATION ONLY.
 *
 * `contracts/adapter.md` §11 forbids re-implementing a transform, and this is
 * not one: nothing here touches a document, a cue or a time. It takes two
 * strings the library already decided are a before/after pair and works out
 * which words to underline, because a two-column view of two nearly identical
 * sentences is unreadable without it — and "cleaning is destructive and
 * invisible without a diff" is the entire reason the diff view exists.
 *
 * `DocDiff.modified[].fields` tells the UI WHICH FIELD changed, which is the
 * right granularity for a column highlight and the wrong one for a sentence.
 * If intra-text spans ever land on `CueDelta`, this file deletes itself and
 * the view renders them instead; that is filed as a contract note rather than
 * assumed.
 *
 * Bounded on purpose: an O(n·m) table over two 4,000-character strings is
 * 16 million cells, and a cleaning pass over 40,000 cues would run it 40,000
 * times. Past the cap the whole string is marked changed, which is both
 * cheaper and, at that length, more honest.
 */

/** Above this, both sides are reported as wholly changed. */
const MAX_TOKENS = 400;

export type Piece =
  | { readonly kind: 'same'; readonly text: string }
  | { readonly kind: 'del'; readonly text: string }
  | { readonly kind: 'ins'; readonly text: string };

/**
 * Split into words AND the whitespace between them, so reassembling the pieces
 * reproduces the input exactly. A diff that drops the spaces would show
 * "changed" for every cue in a file whose only edit was a line reflow.
 */
export function tokenize(text: string): string[] {
  return text.match(/\s+|[^\s]+/g) ?? [];
}

function lcsTable(a: readonly string[], b: readonly string[]): Uint32Array {
  const rows = a.length + 1;
  const cols = b.length + 1;
  const table = new Uint32Array(rows * cols);
  for (let i = a.length - 1; i >= 0; i -= 1) {
    for (let j = b.length - 1; j >= 0; j -= 1) {
      const here = i * cols + j;
      table[here] =
        a[i] === b[j]
          ? (table[(i + 1) * cols + (j + 1)] ?? 0) + 1
          : Math.max(table[(i + 1) * cols + j] ?? 0, table[i * cols + (j + 1)] ?? 0);
    }
  }
  return table;
}

/** Merge neighbouring pieces of the same kind so the DOM gets one node each. */
function coalesce(pieces: Piece[]): Piece[] {
  const out: Piece[] = [];
  for (const piece of pieces) {
    const last = out[out.length - 1];
    if (last && last.kind === piece.kind)
      out[out.length - 1] = { kind: piece.kind, text: last.text + piece.text };
    else out.push(piece);
  }
  return out;
}

export function wordDiff(before: string, after: string): Piece[] {
  if (before === after) return before === '' ? [] : [{ kind: 'same', text: before }];

  const a = tokenize(before);
  const b = tokenize(after);
  if (a.length > MAX_TOKENS || b.length > MAX_TOKENS) {
    const pieces: Piece[] = [];
    if (before) pieces.push({ kind: 'del', text: before });
    if (after) pieces.push({ kind: 'ins', text: after });
    return pieces;
  }

  const cols = b.length + 1;
  const table = lcsTable(a, b);
  const pieces: Piece[] = [];
  let i = 0;
  let j = 0;
  while (i < a.length && j < b.length) {
    if (a[i] === b[j]) {
      pieces.push({ kind: 'same', text: a[i] as string });
      i += 1;
      j += 1;
    } else if ((table[(i + 1) * cols + j] ?? 0) >= (table[i * cols + (j + 1)] ?? 0)) {
      pieces.push({ kind: 'del', text: a[i] as string });
      i += 1;
    } else {
      pieces.push({ kind: 'ins', text: b[j] as string });
      j += 1;
    }
  }
  while (i < a.length) {
    pieces.push({ kind: 'del', text: a[i] as string });
    i += 1;
  }
  while (j < b.length) {
    pieces.push({ kind: 'ins', text: b[j] as string });
    j += 1;
  }
  return coalesce(pieces);
}
